<template>
    <div v-if="token">
        <div class="flex flex-gap-2 align-center">
            <CoreStringInput
                name="token"
                class="flex-grow-1 flex-shrink-1 flex-basis-60"
                :condensed="true"
                v-model="token"
                :disabled="true"
            />
        </div>

        <p class="mt-2 flex items-center gap-2">
            <CoreIconButton
                color="primary"
                tooltip="§ui.tooltip_copy_to_clipboard"
                icon="content-copy"
                @click="copyToClipboard"
            />
            <span class="text-200">
                {{ $t('§iot_connectivity.save_token_message') }}
            </span>
        </p>

        <div class="flex justify-start gap-2 mt-5">
            <CoreButton
                label="§ui_button.create_another_token"
                icon="pi-undo"
                @click="resetInputs"
            />
            <CoreButton
                label="§ui_button.cancel"
                variant="tertiary"
                @click="handleClose"
                icon="pi-times"
            />
        </div>
    </div>

    <div v-else>
        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§iot_connectivity.select_user') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreDropdown
                    name="user"
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    :condensed="true"
                    :options="this.users"
                    v-model="selectedUser"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§iot_connectivity.token_name') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    name="token_name"
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    :condensed="true"
                    v-model="tokenName"
                />
            </template>
        </CoreTwoColumnsRow>

        <div class="flex justify-start gap-2">
            <CoreButton
                label="§ui_button.create"
                icon="pi-check"
                @click="handleCreateToken"
                :loading="tokenStore.saving"
                :disabled="isDisabled"
            />
            <CoreButton label="§ui_button.close" variant="tertiary" @click="handleClose" />
        </div>
    </div>
</template>

<script>
import { useTokenStore } from '@/stores/token'
import { useModalStore } from '@/stores/modal'
import { useOptionsStore } from '@/stores/options'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreStringInput from '@/components/core/CoreStringInput.vue'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreIconButton from '@/components/core/CoreIconButton.vue'

export default {
    name: 'CreateTokenModal',

    components: {
        CoreBorderWrapper,
        CoreTwoColumnsRow,
        CoreStringInput,
        CoreDropdown,
        CoreButton,
        CoreIconButton,
    },

    data() {
        return {
            tokenStore: useTokenStore(),
            modalStore: useModalStore(),
            optionsStore: useOptionsStore(),

            loading: false,
            selectedUser: null,
            tokenName: '',
            token: '',
            users: [],
        }
    },

    computed: {
        isDisabled() {
            return this.selectedUser === null || this.tokenName === '' || this.tokenStore.saving
        },
    },

    mounted() {
        this.users = this.modalStore.data.users
    },

    methods: {
        async handleCreateToken() {
            const token = await this.tokenStore.create(this.selectedUser, this.tokenName)
            this.token = token
        },

        handleClose() {
            this.modalStore.setClose()
        },

        async copyToClipboard() {
            const triggerNotification = (severity, detail) => {
                this.$toast.add({
                    severity,
                    summary: severity === 'error' ? 'Error' : 'Success',
                    detail,
                    life: 1000,
                })
            }

            const isAllowed = await this.checkClipboardPermission()

            if (isAllowed) {
                navigator.clipboard.writeText(this.token).then(
                    function () {
                        triggerNotification('success', 'notifications.success_clipboard_copy')
                    },
                    function () {
                        triggerNotification('error', 'notifications.error_clipboard_copy')
                    }
                )
            }
        },

        checkClipboardPermission() {
            // firefox has clipboard-write permission true by default
            if (navigator.userAgent.indexOf('Firefox') > -1) {
                return true
            }

            // safari does not support permission API
            if (navigator.userAgent.indexOf('Safari') > -1) {
                return true
            }

            const result = navigator.permissions.query({
                name: 'clipboard-write',
            })

            return result
        },

        resetInputs() {
            this.selectedUser = null
            this.tokenName = ''
            this.token = ''
        },
    },
}
</script>
